// Context-picker state for the SQ-AI chat input.
//
// Holds the chat-session-level scope the user pins via the picker — selected
// modules and (when exactly one module is pinned) a template subset — plus the
// data needed to render it: the tenant's askable modules and each module's
// template list (lazy-loaded). Produces the `context` object sent with every
// turn (chip_context_wiring_spec.md).
//
// State lives here (lifted into SqAiPage) so it persists across turns and is
// injected into each send. It resets when `resetKey` changes — SqAiPage passes
// the conversationId, which a new chat OR any scope change (env/tenant/conf/tz)
// already rotates, so chips clear exactly when the scope they describe does.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { aiAssistantApi } from "../../services/api";

// Form-driven modules have templates; tasks are flat records with none
// (B-VERSION-5). Keep in sync with the server's TEMPLATE_TABLES (pickerContext.js).
const MODULES_WITH_TEMPLATES = new Set(["issues", "incidents", "inspections", "risk_assessments", "ptw"]);

export function moduleHasTemplates(canonical) {
  return MODULES_WITH_TEMPLATES.has(canonical);
}

function errMessage(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message) ||
    (err && err.message) || fallback;
}

export function usePicker({ env, tenantSchema, resetKey }) {
  // Available modules for this tenant — labels + disabled already resolved
  // server-side (the picker just renders them).
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState(null);

  // Pinned scope. selected = ordered canonical ids; templates = template titles
  // (only meaningful when exactly one module is pinned).
  const [selected, setSelected] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [note, setNote] = useState(null);

  // Template lists per module, lazy-loaded + cached for the session. Held in a
  // ref (source of truth) with a render counter so reads during render stay
  // fresh without re-subscribing effects.
  const tplCacheRef = useRef(new Map()); // canonical -> { loading, error, items: [{id,title}] | null }
  const [, bumpTpl] = useReducer((x) => x + 1, 0);

  // Load the tenant's askable modules whenever the scope changes.
  useEffect(() => {
    if (!tenantSchema) {
      setAvailable([]);
      setAvailableError(null);
      return;
    }
    let cancelled = false;
    setAvailableLoading(true);
    setAvailableError(null);
    aiAssistantApi
      .getContextModules(env, tenantSchema)
      .then((res) => {
        if (cancelled) return;
        setAvailable(Array.isArray(res.data && res.data.modules) ? res.data.modules : []);
        setAvailableLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setAvailable([]);
        setAvailableError(errMessage(err, "Could not load modules"));
        setAvailableLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, tenantSchema]);

  // Reset the pinned scope when the chat/scope resets.
  useEffect(() => {
    setSelected([]);
    setTemplates([]);
    setNote(null);
    tplCacheRef.current = new Map();
    bumpTpl();
  }, [resetKey]);

  const labelOf = useCallback(
    (canonical) => {
      const m = available.find((x) => x.canonical === canonical);
      return m ? m.label : canonical;
    },
    [available],
  );

  const toggleModule = useCallback(
    (canonical) => {
      const next = selected.includes(canonical) ? selected.filter((c) => c !== canonical) : [...selected, canonical];
      setSelected(next);
      if (next.length === 1) {
        // Back to a single module — the template control returns; drop any
        // stale "templates cleared" note.
        setNote(null);
      } else {
        // 0 or 2+ modules: templates no longer apply. The note fires ONLY for
        // the multi-module case where templates were actually lost (going to 0
        // clears silently, matching the chip-✕ path).
        setNote(templates.length && next.length > 1 ? "Templates cleared — multiple modules selected." : null);
        setTemplates([]);
      }
    },
    [selected, templates],
  );

  const removeModule = useCallback(
    (canonical) => {
      const next = selected.filter((c) => c !== canonical);
      setSelected(next);
      if (next.length !== 1) setTemplates([]);
      // Removing a chip is an explicit user action — never the note's trigger.
      setNote(null);
    },
    [selected],
  );

  // Add one or more modules atomically (a single setSelected) — used by the #2a
  // ADD path (backend "compare X and Y" broadening). A per-call loop of toggleModule
  // would derive each step from the same stale render snapshot and drop all but the
  // last; this handles the whole batch in one update. Going to 2+ modules clears
  // templates (they no longer apply), matching toggleModule.
  const addModules = useCallback(
    (canonicals) => {
      const list = (Array.isArray(canonicals) ? canonicals : [canonicals]).filter(Boolean);
      const fresh = list.filter((c) => !selected.includes(c));
      if (!fresh.length) return;
      const next = [...selected, ...fresh];
      setSelected(next);
      if (next.length > 1) {
        if (templates.length) setNote("Templates cleared — multiple modules selected.");
        setTemplates([]);
      }
    },
    [selected, templates],
  );

  const dismissNote = useCallback(() => setNote(null), []);

  // Lazy-load a module's templates (once per module, cached for the session).
  const loadTemplates = useCallback(
    (canonical) => {
      if (!moduleHasTemplates(canonical) || !tenantSchema) return;
      const cache = tplCacheRef.current;
      const entry = cache.get(canonical);
      if (entry && (entry.loading || entry.items)) return; // already loading/loaded
      cache.set(canonical, { loading: true, error: null, items: null });
      bumpTpl();
      aiAssistantApi
        .getContextTemplates(env, tenantSchema, canonical)
        .then((res) => {
          cache.set(canonical, {
            loading: false,
            error: null,
            items: Array.isArray(res.data && res.data.templates) ? res.data.templates : [],
          });
          bumpTpl();
        })
        .catch((err) => {
          // items stays null (not []) so the line-above guard treats an errored
          // entry as not-loaded — reopening the panel retries instead of sticking.
          cache.set(canonical, { loading: false, error: errMessage(err, "Could not load templates"), items: null });
          bumpTpl();
        });
    },
    [env, tenantSchema],
  );

  const getTemplateEntry = useCallback((canonical) => tplCacheRef.current.get(canonical) || null, []);

  // The context object sent with every turn. `templates` is present only when
  // exactly one module is pinned (empty array = all templates of that module).
  const context = useMemo(
    () => ({
      tenant: tenantSchema || null,
      modules: selected.map((c) => ({ canonical: c, label: labelOf(c) })),
      templates: selected.length === 1 ? templates : [],
    }),
    [tenantSchema, selected, templates, labelOf],
  );

  return {
    available,
    availableLoading,
    availableError,
    selected,
    templates,
    note,
    toggleModule,
    addModules,
    removeModule,
    setTemplates,
    dismissNote,
    loadTemplates,
    getTemplateEntry,
    context,
  };
}
